import { useEffect, useState } from 'react';

import { getIncodeVerificationDocuments } from 'PrivateApis';

import { SENTRY_TAG } from '../IncodeSDKHelpers';

interface UseIncodeVerificationDocumentsProps {
  authToken?: string;
  payload?: { enquiryId?: number | null };
}

export const INCODE_VERIFICATION_DOCUMENTS_STATUS = {
  COMPLETED: 'completed',
  CONTINUE: 'continue',
  START: 'start',
} as const;

export type IncodeVerificationDocumentsStatus = typeof INCODE_VERIFICATION_DOCUMENTS_STATUS[
    keyof typeof INCODE_VERIFICATION_DOCUMENTS_STATUS
  ] | undefined;

export const useIncodeVerificationDocuments = ({
  authToken,
  payload,
}: UseIncodeVerificationDocumentsProps) => {
  const [status, setStatus] = useState<IncodeVerificationDocumentsStatus>(undefined);
  const [data, setData] = useState<any>(undefined);
  const [isLoading, setIsLoading] = useState(true);

  const enquiryId = payload?.enquiryId;

  useEffect(() => {
    if (!authToken || !enquiryId) {
      setIsLoading(false);
      setData(undefined);
      setStatus(undefined);
      return () => {};
    }

    const abortController = new AbortController();
    const { signal } = abortController;

    const fetchDocuments = () => {
      setIsLoading(true);
      setStatus(undefined);

      getIncodeVerificationDocuments(
        { Authorization: authToken },
        { enquiryId },
        abortController,
      )
        .then((response) => {
          if (!signal.aborted) {
            const firstVerification = response?.data?.[0] || undefined;

            if (firstVerification) {
              const { completedSteps = [], requiredSteps = [] } = firstVerification;

              const completedCount = completedSteps.length;
              const requiredCount = requiredSteps.length;

              let finalStatus;

              switch (true) {
              case completedCount === 0:
                finalStatus = INCODE_VERIFICATION_DOCUMENTS_STATUS.START;
                break;

              case completedCount === requiredCount:
                finalStatus = INCODE_VERIFICATION_DOCUMENTS_STATUS.COMPLETED;
                break;

              default:
                finalStatus = INCODE_VERIFICATION_DOCUMENTS_STATUS.CONTINUE;
              }

              setData(firstVerification);
              setStatus(finalStatus);
              return;
            }

            setData(undefined);
            setStatus(INCODE_VERIFICATION_DOCUMENTS_STATUS.START);
          }
        })
        .catch((err) => {
          if (!signal.aborted) {
            globalThis?.Sentry?.captureException?.(err, SENTRY_TAG);
            setData(undefined);
            setStatus(INCODE_VERIFICATION_DOCUMENTS_STATUS.START);
          }
        })
        .then(() => {
          if (!signal.aborted) {
            setIsLoading(false);
          }
        });
    };

    fetchDocuments();

    return () => {
      abortController.abort();
    };
  }, [authToken, enquiryId, payload]);

  return { data, isLoading, status };
};
